import { useEffect, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Check, RefreshCw, BadgeCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useToast } from "../hooks/use-toast";

const STATUS_BADGE = {
  pending: "tier-exposed",
  paid: "tier-performing",
};

export default function AdminWithdrawals() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [marking, setMarking] = useState({});

  const fetchRows = useCallback(async () => {
    try {
      const url = statusFilter === "all" ? "/admin/withdrawals" : `/admin/withdrawals?status=${statusFilter}`;
      const { data } = await api.get(url);
      setRows(data);
    } catch (e) {
      toast({ title: "Load failed", description: formatApiError(e), variant: "destructive" });
    }
  }, [statusFilter, toast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const markPaid = async (id) => {
    setMarking((m) => ({ ...m, [id]: true }));
    try {
      await api.post(`/admin/withdrawals/${id}/mark-paid`, {});
      toast({ title: "Marked as paid" });
      await fetchRows();
    } catch (e) {
      toast({ title: "Mark paid failed", description: formatApiError(e), variant: "destructive" });
    } finally {
      setMarking((m) => ({ ...m, [id]: false }));
    }
  };

  const totalPending = rows.filter((r) => r.status === "pending").reduce((s, r) => s + (r.amount || 0), 0);

  return (
    <section className="glass rounded-2xl p-6" data-testid="admin-withdrawals-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-xl">Withdrawal Requests</h2>
          <p className="text-xs text-slate-500 mt-1">
            Manual ledger of affiliate payout requests. Use this to reconcile your Stripe transfers on the 1st of each month.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32 bg-white/5 border-white/10" data-testid="withdrawals-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0D1B2A] border-white/10 text-white">
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchRows} variant="outline" className="rounded-full bg-transparent border-white/15" data-testid="withdrawals-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 text-sm">
        <span className="text-slate-500">Pending total:</span>
        <span className="font-display text-lg text-white" data-testid="withdrawals-pending-total">
          £{totalPending.toFixed(2)}
        </span>
      </div>

      <div className="overflow-x-auto rounded-xl border border-white/5">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-slate-400">Affiliate</TableHead>
              <TableHead className="text-slate-400">Amount</TableHead>
              <TableHead className="text-slate-400">Status</TableHead>
              <TableHead className="text-slate-400">Requested</TableHead>
              <TableHead className="text-slate-400">Paid</TableHead>
              <TableHead className="text-slate-400">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow className="border-white/5">
                <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                  No {statusFilter === "all" ? "" : statusFilter + " "}withdrawal requests.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} className="border-white/5 hover:bg-white/[0.03]" data-testid={`withdrawal-row-${r.id}`}>
                  <TableCell className="font-medium text-white">{r.email}</TableCell>
                  <TableCell className="font-mono">£{(r.amount || 0).toFixed(2)} {r.currency}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded-full text-xs border ${STATUS_BADGE[r.status] || "tier-exposed"}`}>
                      {r.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs text-slate-400">{new Date(r.requested_at).toLocaleString("en-GB")}</TableCell>
                  <TableCell className="text-xs text-slate-400">
                    {r.paid_at ? new Date(r.paid_at).toLocaleString("en-GB") : "—"}
                  </TableCell>
                  <TableCell>
                    {r.status === "pending" ? (
                      <Button
                        size="sm"
                        onClick={() => markPaid(r.id)}
                        disabled={!!marking[r.id]}
                        className="btn-glow rounded-full h-8 text-xs"
                        data-testid={`mark-paid-${r.id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" /> {marking[r.id] ? "…" : "Mark Paid"}
                      </Button>
                    ) : (
                      <span className="text-tier-performing text-xs inline-flex items-center">
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Settled
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
